// TelcoTrack — shell navigation model.
// Home is the Dashboard přehled. Modules are expandable sidebar categories,
// except Mapa, which is one link to /maps. There is no card landing. A nested
// entity (company, polygon, warehouse, …) keeps every category visible: the
// current one stays open and shows a back link one step up plus that level's
// tabs. The top bar is a breadcrumb. Nastavení is a sidebar gear. While
// `/app-nastaveni` is open it is the only category; leaving that route
// brings the modules back.

import {
  canOpenCompanyList,
  canViewOtherUsersActivityAndWorkReport,
  canReadCompanyEarnings,
  canViewEquipmentSection,
  canViewMaterialsSection,
  hasOrgAdminRole,
  hasWarehouseApiAccessRole,
  hasSuperAdminRole,
  loadedPermissionProfile,
  permissionAllows,
  type AppPermissionSectionId,
} from '@core/shared-bridge';
import type { TopbarSectionTabRow } from '../services/topbar-section-tabs.service';
import { EQUIPMENT_HUB_UI_ENABLED } from '../../features/equipment/equipment-hub.flags';
import { iconPaths } from '../../shared/utils/icon-vocabulary';
import { navIconPaths, type NavIconId } from '../../shared/utils/nav-icons';

/** Legacy URL. The router redirects it to the Dashboard přehled. */
export const SHELL_HOME_PATH = '/home';
/** Home icon, post-login, and the `/home` redirect. Dashboard přehled. */
export const SHELL_LANDING_PATH = '/dashboard';
/** Home-icon label. The page on `SHELL_LANDING_PATH` is titled Přehled. */
export const SHELL_HOME_LABEL = 'Přehled';
export const SHELL_HOME_TITLE = SHELL_HOME_LABEL;
export const SHELL_HOME_SUBTITLE = 'Čísla na jeden pohled';

export interface ShellSectionDef {
  id: string;
  label: string;
  /** Absolute path of the existing screen. */
  route: string;
  icon: NavIconId;
  /** Key in `shellAccessFlags`. Several sections may share one flag. */
  accessId: string;
  /** Overrides `icon` when the glyph is not in the shared nav set. */
  iconPaths?: readonly string[];
}

export interface ShellModuleDef {
  id: string;
  label: string;
  description: string;
  /**
   * Fallback path for the module crumb. Home opens the first section the user
   * can actually see (`shellModuleEntryRoute`).
   */
  route: string;
  icon: NavIconId;
  /** Nastavení is reachable from the sidebar, not from a home card. */
  homeCard?: boolean;
  /**
   * Permission key for a leaf module (`sections` is empty). Same keys as
   * `shellAccessFlags`. A module with children uses each section's `accessId`.
   */
  accessId?: string;
  sections: readonly ShellSectionDef[];
}

const ICON_PRIPOJENI = [
  'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
] as const;
const ICON_VZHLED = [
  'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
] as const;

/** Sidebar categories, top to bottom. Not a card page. */
export const SHELL_HOME_MODULES: readonly ShellModuleDef[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Celkový přehled, Gantt, finance a materiál.',
    route: '/dashboard',
    icon: 'dashboard',
    sections: [
      { id: 'prehled', label: 'Celkový přehled', route: '/dashboard', icon: 'objects', accessId: 'dashboard' },
      {
        id: 'gantt',
        label: 'Gantt',
        route: '/dashboard/gantt',
        icon: 'dashboard',
        accessId: 'dashboard',
        iconPaths: [
          'M4 6h9a1 1 0 011 1v1.5a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1zm5 5.5h10a1 1 0 011 1V14a1 1 0 01-1 1H9a1 1 0 01-1-1v-1.5a1 1 0 011-1zM6 17h8a1 1 0 011 1v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-1a1 1 0 011-1z',
        ],
      },
      { id: 'finance', label: 'Finance', route: '/dashboard/finance', icon: 'finance', accessId: 'dashboard-finance' },
      {
        id: 'material',
        label: 'Materiál',
        route: '/dashboard/material',
        icon: 'materials',
        accessId: 'dashboard-material',
      },
    ],
  },
  {
    id: 'management',
    label: 'Management',
    description: 'Fakturace, fronta práce a navolávání.',
    route: '/fakturace',
    icon: 'management',
    sections: [
      { id: 'fakturace', label: 'Fakturace', route: '/fakturace', icon: 'fakturace', accessId: 'fakturace' },
      { id: 'fronta-prace', label: 'Fronta práce', route: '/fronta-prace', icon: 'fronta-prace', accessId: 'fronta-prace' },
      { id: 'navolavani', label: 'Navolávání', route: '/navolavani', icon: 'navolavani', accessId: 'navolavani' },
    ],
  },
  {
    id: 'mapa',
    label: 'Mapa',
    description: 'Mapa sítě a objekty v terénu.',
    route: '/maps',
    icon: 'maps',
    accessId: 'maps',
    sections: [],
  },
  {
    id: 'stavby',
    label: 'Stavby',
    description: 'Polygony, objekty, deník, nedodělky a změny oproti PD.',
    route: '/polygons',
    icon: 'polygons',
    sections: [
      { id: 'polygons', label: 'Polygony', route: '/polygons', icon: 'polygons', accessId: 'polygons' },
      { id: 'objects', label: 'Objekty', route: '/objects', icon: 'objects', accessId: 'objects' },
      { id: 'diary', label: 'Stavební deník', route: '/diary', icon: 'diary', accessId: 'diary' },
      { id: 'nedodelky', label: 'Nedodělky', route: '/nedodelky', icon: 'nedodelky', accessId: 'diary' },
      { id: 'pd-changes', label: 'Změny oproti PD', route: '/pd-changes', icon: 'pd-changes', accessId: 'pd-changes' },
    ],
  },
  {
    id: 'priprava',
    label: 'Příprava',
    description: 'Inženýring, příprava, email a datovka.',
    route: '/urady/inzenyring',
    icon: 'priprava',
    sections: [
      { id: 'inzenyring', label: 'Inženýring', route: '/urady/inzenyring', icon: 'inzenyring', accessId: 'inzenyring' },
      { id: 'priprava', label: 'Příprava', route: '/urady/priprava', icon: 'priprava', accessId: 'priprava' },
      { id: 'email', label: 'Email', route: '/urady/komunikace/email', icon: 'email', accessId: 'email' },
      { id: 'datovka', label: 'Datovka', route: '/urady/komunikace/datovka', icon: 'datovka', accessId: 'datovka' },
    ],
  },
  {
    id: 'hr',
    label: 'HR',
    description: 'Uživatelé, kontakty, firmy, kalendář, výkaz práce, výdělek firmy a ceník.',
    route: '/users',
    icon: 'users',
    sections: [
      { id: 'users', label: 'Uživatelé', route: '/users', icon: 'users', accessId: 'users' },
      { id: 'kontakty', label: 'Kontakty', route: '/users/kontakty', icon: 'kontakty', accessId: 'kontakty' },
      { id: 'firmy', label: 'Firmy', route: '/users/firmy', icon: 'companies', accessId: 'firmy' },
      { id: 'kalendar', label: 'Kalendář', route: '/users/kalendar', icon: 'diary', accessId: 'kalendar' },
      { id: 'work-report', label: 'Výkaz práce', route: '/work-report', icon: 'work-report', accessId: 'work-report' },
      { id: 'vydelek-firmy', label: 'Výdělek firmy', route: '/vydelek-firmy', icon: 'finance', accessId: 'vydelek-firmy' },
      { id: 'cenik', label: 'Ceník', route: '/cenik', icon: 'finance', accessId: 'cenik' },
    ],
  },
  {
    id: 'logistika',
    label: 'Logistika',
    description: 'Zařízení a sklady.',
    route: '/materials',
    icon: 'materials',
    sections: [
      { id: 'equipment', label: 'Zařízení', route: '/equipment', icon: 'equipment', accessId: 'equipment' },
      { id: 'materials', label: 'Sklady', route: '/materials', icon: 'materials', accessId: 'materials' },
    ],
  },
  {
    id: 'dev',
    label: 'Dev',
    description: 'Import dat a prohlížení databáze.',
    route: '/import',
    icon: 'database',
    sections: [
      { id: 'import', label: 'Import', route: '/import', icon: 'import', accessId: 'import' },
      { id: 'database', label: 'Databáze', route: '/admin/database', icon: 'database', accessId: 'database' },
    ],
  },
];

/** Sidebar-only. Not a home card. */
export const SHELL_SETTINGS: ShellModuleDef = {
  id: 'app-settings',
  label: 'Nastavení',
  description: 'Připojení a vzhled aplikace.',
  route: '/app-nastaveni/pripojeni',
  icon: 'settings',
  homeCard: false,
  sections: [
    {
      id: 'pripojeni',
      label: 'Připojení',
      route: '/app-nastaveni/pripojeni',
      icon: 'settings',
      accessId: 'app-settings',
      iconPaths: ICON_PRIPOJENI,
    },
    {
      id: 'vzhled',
      label: 'Vzhled',
      route: '/app-nastaveni/vzhled',
      icon: 'settings',
      accessId: 'app-settings',
      iconPaths: ICON_VZHLED,
    },
  ],
};

const ALL_MODULES: readonly ShellModuleDef[] = [...SHELL_HOME_MODULES, SHELL_SETTINGS];

interface SectionMatch {
  module: ShellModuleDef;
  section: ShellSectionDef | null;
}

const SECTIONS_BY_ROUTE_LENGTH: readonly SectionMatch[] = ALL_MODULES
  .flatMap((module) => module.sections.map((section) => ({ module, section })))
  .sort((a, b) => (b.section?.route.length ?? 0) - (a.section?.route.length ?? 0));

export function shellModuleIconPaths(mod: ShellModuleDef): readonly string[] {
  return navIconPaths(mod.icon);
}

export function shellSectionIconPaths(section: ShellSectionDef): readonly string[] {
  return section.iconPaths?.length ? section.iconPaths : navIconPaths(section.icon);
}

/** Subcategory glyphs (polygon tabs, company tabs, other in-level rows). Stroke matches the sidebar icons. */
const TAB_ICON_CHART = [
  'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
] as const;
const TAB_ICON_LIST = ['M4 6h16M4 12h16M4 18h12'] as const;
const TAB_ICON_GANTT = [
  'M4 6h9a1 1 0 011 1v1.5a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1zm5 5.5h10a1 1 0 011 1V14a1 1 0 01-1 1H9a1 1 0 01-1-1v-1.5a1 1 0 011-1zM6 17h8a1 1 0 011 1v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-1a1 1 0 011-1z',
] as const;
const TAB_ICON_PULSE = ['M3 12h4l2.5-6 4.5 12 2.5-6H21'] as const;
/** Rozvaděč — shared vocabulary (`rozvadec`). */
const TAB_ICON_NODES = iconPaths('rozvadec');
const TAB_ICON_FILE = [
  'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
] as const;
const TAB_ICON_LANDMARK = [
  'M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z',
] as const;
/** Datum — same calendar as the date-range filter. */
const TAB_ICON_CALENDAR = iconPaths('calendar');
/** Zásoby — three stacked boxes (Heroicons square-3-stack-3d). */
const TAB_ICON_STACK = [
  'M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3',
] as const;
/** Sklady — warehouse / storefront. */
const TAB_ICON_WAREHOUSE = [
  'M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z',
] as const;
/** Příjem — arrow down into a tray. */
const TAB_ICON_TRAY_IN = [
  'M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3',
] as const;
/** Výdej — arrow up out of a tray. */
const TAB_ICON_TRAY_OUT = [
  'M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5',
] as const;
/** Převod — two opposing arrows. */
const TAB_ICON_TRANSFER = [
  'M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5',
] as const;
/** Štítek — Ceny and Akvizice (`tag` in the icon vocabulary). */
const TAB_ICON_TAG = iconPaths('tag');
/** Doplnění zásob — closed box with a plus. */
const TAB_ICON_BOX_PLUS = [
  'm20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z',
  'M12 11.25v5.5M9.25 14h5.5',
] as const;
/** Příjem a výdej — arrows both ways. */
const TAB_ICON_FLOW = [
  'M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5',
] as const;
/** Pod minimem — warning triangle. */
const TAB_ICON_ALERT = [
  'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z',
] as const;
/** Břemena — balance scale (Heroicons outline). The set had no weight glyph. */
const TAB_ICON_SCALE = [
  'M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971Zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971Z',
] as const;
/** Kontakty / Profil / Moje — single person (account glyph in the top bar). */
const TAB_ICON_USER = [
  'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
] as const;
/** Historie — clock from the map activity control. */
const TAB_ICON_CLOCK = [
  'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
] as const;
/** Fáze / stav — same check-circle as the phase filter. */
const TAB_ICON_CHECK = iconPaths('status');
/** Výkop — pit outline from the map jáma glyph. */
const TAB_ICON_PIT = [
  'M8 8h8l1.5 2.5L15 18H9L6.5 10.5 8 8z',
  'M7 12h10',
] as const;
/** Svar / provaření / spojkoviště — shared vocabulary (`splice`). */
const TAB_ICON_SPLICE = iconPaths('splice');

const TAB_ICONS_BY_KEY: Record<string, readonly string[]> = {
  dashboard: TAB_ICON_CHART,
  prehled: navIconPaths('objects'),
  přehled: navIconPaths('objects'),
  gantt: TAB_ICON_GANTT,
  finance: navIconPaths('finance'),
  workreport: navIconPaths('work-report'),
  'výkaz práce': navIconPaths('work-report'),
  material: navIconPaths('materials'),
  materiál: navIconPaths('materials'),
  hodnota: TAB_ICON_SCALE,
  'prijem-vydej': TAB_ICON_FLOW,
  'příjem a výdej': TAB_ICON_FLOW,
  'pod-minimem': TAB_ICON_ALERT,
  'pod minimem': TAB_ICON_ALERT,
  activity: TAB_ICON_PULSE,
  aktivita: TAB_ICON_PULSE,
  rozvadec: TAB_ICON_NODES,
  rozvaděče: TAB_ICON_NODES,
  pripojeni: iconPaths('budova'),
  'objekty k připojení': iconPaths('budova'),
  documents: TAB_ICON_FILE,
  dokumenty: TAB_ICON_FILE,
  urady: TAB_ICON_LANDMARK,
  úřady: TAB_ICON_LANDMARK,
  uzivatele: navIconPaths('users'),
  uživatelé: navIconPaths('users'),
  users: navIconPaths('users'),
  pozvanky: navIconPaths('email'),
  pozvánky: navIconPaths('email'),
  cenik: navIconPaths('finance'),
  ceník: navIconPaths('finance'),
  kalendar: TAB_ICON_CALENDAR,
  kalendář: TAB_ICON_CALENDAR,
  'warehouse-stock': TAB_ICON_STACK,
  zásoby: TAB_ICON_STACK,
  catalog: navIconPaths('objects'),
  katalog: navIconPaths('objects'),
  warehouses: TAB_ICON_WAREHOUSE,
  sklady: TAB_ICON_WAREHOUSE,
  products: navIconPaths('materials'),
  'warehouse-receipts': TAB_ICON_TRAY_IN,
  'warehouse-issues': TAB_ICON_TRAY_OUT,
  'warehouse-transfers': TAB_ICON_TRANSFER,
  převod: TAB_ICON_TRANSFER,
  'warehouse-inventory': navIconPaths('priprava'),
  inventury: navIconPaths('priprava'),
  'price-comparison': TAB_ICON_TAG,
  ceny: TAB_ICON_TAG,
  replenishment: TAB_ICON_BOX_PLUS,
  'doplnění zásob': TAB_ICON_BOX_PLUS,
  bremena: TAB_ICON_SCALE,
  'břemena': TAB_ICON_SCALE,
  'dotcene-organy': TAB_ICON_LANDMARK,
  'dotčené orgány': TAB_ICON_LANDMARK,
  kontakty: iconPaths('kontakty'),
  'krizeni-siti': TAB_ICON_NODES,
  'křížení sítí': TAB_ICON_NODES,
  vytyceni: navIconPaths('maps'),
  'vytyčení': navIconPaths('maps'),
  povinnosti: navIconPaths('priprava'),
  posta: navIconPaths('email'),
  email: navIconPaths('email'),
  datovka: navIconPaths('datovka'),
  'k-schvaleni': TAB_ICON_CHECK,
  'ke schválení': TAB_ICON_CHECK,
  'všechny objekty': navIconPaths('objects'),
  budova: iconPaths('budova'),
  vykop: TAB_ICON_PIT,
  'výkop': TAB_ICON_PIT,
  spojkoviste: TAB_ICON_SPLICE,
  'spojkoviště': TAB_ICON_SPLICE,
  'všechny': navIconPaths('diary'),
  mine: TAB_ICON_USER,
  moje: TAB_ICON_USER,
  stavby: navIconPaths('polygons'),
  zavrt: TAB_ICON_PIT,
  'závrt': TAB_ICON_PIT,
  'k-zavrtu': TAB_ICON_PIT,
  'k závrtu': TAB_ICON_PIT,
  'budovy k závrtu': TAB_ICON_PIT,
  digitalni_cesta: TAB_ICON_PULSE,
  'digitalni-cesta': TAB_ICON_PULSE,
  'digitální cesta': TAB_ICON_PULSE,
  svar_budovy: TAB_ICON_SPLICE,
  'svár': TAB_ICON_SPLICE,
  // Záfuk — cable grid from the vocabulary, not the folded map and not převod arrows.
  zafuk: iconPaths('zafuk'),
  'záfuk': iconPaths('zafuk'),
  'budovy k zafouknutí': iconPaths('zafuk'),
  provareni: TAB_ICON_SPLICE,
  'budovy k provaření': TAB_ICON_SPLICE,
  notes: iconPaths('note'),
  'poznámky': iconPaths('note'),
  'hp-count': iconPaths('budova'),
  'počet hp': iconPaths('budova'),
  navaznost: TAB_ICON_NODES,
  'návaznost na rozvaděč': TAB_ICON_NODES,
  navaznosti: ICON_PRIPOJENI,
  'návaznosti': ICON_PRIPOJENI,
  odbocky: ICON_PRIPOJENI,
  'odbočky': ICON_PRIPOJENI,
  'k-odboceni': ICON_PRIPOJENI,
  'k odbočení': ICON_PRIPOJENI,
  'budovy k odbočení': ICON_PRIPOJENI,
  // Fronta práce — one existing stroke glyph per polygon tab.
  budovy: iconPaths('budova'),
  // Zřízené VR — vertikální rozvody (same stack as Zásoby).
  vr: TAB_ICON_STACK,
  'vertikální rozvody': TAB_ICON_STACK,
  'rozvadec-postaveni': TAB_ICON_NODES,
  'rozvaděče k postavení': TAB_ICON_NODES,
  'rozvadec-zafuk': iconPaths('zafuk'),
  'rozvaděče k zafouknutí': iconPaths('zafuk'),
  'rozvadec-provareni': TAB_ICON_SPLICE,
  'rozvaděče k provaření': TAB_ICON_SPLICE,
  polygons: navIconPaths('polygons'),
  polygony: navIconPaths('polygons'),
  geojson: navIconPaths('maps'),
  geometrie: navIconPaths('maps'),
  objects: iconPaths('budova'),
  'připojované objekty': iconPaths('budova'),
  ppd: TAB_ICON_FILE,
  akvizice: iconPaths('tag'),
  'hledání vlastníků': iconPaths('tag'),
  'equipment-stock': navIconPaths('work-report'),
  registr: navIconPaths('work-report'),
  'equipment-categories': navIconPaths('companies'),
  kategorie: navIconPaths('companies'),
  'equipment-receipts': TAB_ICON_TRAY_IN,
  'příjem': TAB_ICON_TRAY_IN,
  'equipment-issues': TAB_ICON_TRAY_OUT,
  'výdej': TAB_ICON_TRAY_OUT,
  'equipment-transfers': TAB_ICON_TRANSFER,
  'převodky': TAB_ICON_TRANSFER,
  'equipment-catalog': navIconPaths('materials'),
  'produkty': navIconPaths('materials'),
  'equipment-calendar': TAB_ICON_CALENDAR,
  'kalendář zařízení': TAB_ICON_CALENDAR,
  profile: TAB_ICON_USER,
  profil: TAB_ICON_USER,
  assetcard: navIconPaths('equipment'),
  'majetková karta': navIconPaths('equipment'),
  timeline: TAB_ICON_CLOCK,
  historie: TAB_ICON_CLOCK,
  statistics: TAB_ICON_CHART,
  statistiky: TAB_ICON_CHART,
  kpi: navIconPaths('dashboard'),
  held: navIconPaths('equipment'),
  pending: TAB_ICON_CHECK,
  protocols: TAB_ICON_FILE,
};

export function shellTabIconPaths(tab: { id: string; label: string }): readonly string[] {
  const id = tab.id.trim().toLocaleLowerCase('cs');
  const label = tab.label.trim().toLocaleLowerCase('cs');
  return TAB_ICONS_BY_KEY[id] ?? TAB_ICONS_BY_KEY[label] ?? TAB_ICON_LIST;
}

export function areShellTabItemsEqual(
  a: readonly { id: string; label: string; disabled?: boolean; panelId?: string }[],
  b: readonly { id: string; label: string; disabled?: boolean; panelId?: string }[],
): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (
      left.id !== right.id ||
      left.label !== right.label ||
      Boolean(left.disabled) !== Boolean(right.disabled) ||
      (left.panelId ?? '') !== (right.panelId ?? '')
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Polygon dashboard tabs live on one route (`?tab=`).
 * Dashboard clears `tab`. Every other row sets it. Other query keys (returnUrl) stay.
 */
export function shellEntityTabLink(
  url: string,
  idPrefix: string,
  tabId: string,
): { commands: string[]; queryParams: Record<string, string | null> } | null {
  if (idPrefix !== 'pd-tab') return null;
  const match = /^\/polygons\/([^/]+)/.exec(shellPathname(url));
  const rawId = match?.[1];
  if (!rawId) return null;
  let polygonId = rawId;
  try {
    polygonId = decodeURIComponent(rawId);
  } catch {
    polygonId = rawId;
  }
  return {
    commands: ['/polygons', polygonId, 'dashboard'],
    queryParams: { tab: tabId === 'dashboard' ? null : tabId },
  };
}

/**
 * Absolute href for one polygon sidebar row.
 * Built from the current URL so `returnUrl` and any other query keys survive.
 */
export function shellEntityTabHref(routerUrl: string, idPrefix: string, tabId: string): string | null {
  const link = shellEntityTabLink(routerUrl, idPrefix, tabId);
  if (!link) return null;
  const hashAt = routerUrl.indexOf('#');
  const hash = hashAt >= 0 ? routerUrl.slice(hashAt) : '';
  const withoutHash = hashAt >= 0 ? routerUrl.slice(0, hashAt) : routerUrl;
  const query = withoutHash.includes('?') ? withoutHash.slice(withoutHash.indexOf('?') + 1) : '';
  const params = new URLSearchParams(query);
  const tab = link.queryParams['tab'];
  if (tab == null || tab === '') params.delete('tab');
  else params.set('tab', tab);
  const path = `/${link.commands.map((part) => encodeURIComponent(String(part).replace(/^\/+/, ''))).join('/')}`;
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ''}${hash}`;
}

export interface ShellAccessUser {
  roles?: readonly string[] | null;
  permissionProfile?: unknown;
}

/** A leaf module is the link itself. It has no chevron and no child rows. */
export function shellModuleIsLeaf(mod: ShellModuleDef): boolean {
  return mod.sections.length === 0;
}

/** A module is visible when the user can open it, or at least one of its sections. */
export function shellModuleVisible(moduleId: string, user: ShellAccessUser | null | undefined): boolean {
  const mod = ALL_MODULES.find((item) => item.id === moduleId);
  if (!mod) return false;
  if (shellModuleIsLeaf(mod)) return !!mod.accessId && shellSectionVisible(mod.accessId, user);
  return shellVisibleSections(mod, user).length > 0;
}

/** Section flag. `accessId` is the key in `shellAccessFlags`, not always the section id. */
export function shellSectionVisible(accessId: string, user: ShellAccessUser | null | undefined): boolean {
  return shellAccessFlags(user)[accessId] === true;
}

export function shellVisibleSections(
  mod: ShellModuleDef,
  user: ShellAccessUser | null | undefined,
): ShellSectionDef[] {
  return mod.sections.filter((section) => shellSectionVisible(section.accessId, user));
}

/** Home card target: first section this user may open. */
export function shellModuleEntryRoute(mod: ShellModuleDef, user: ShellAccessUser | null | undefined): string {
  return shellVisibleSections(mod, user)[0]?.route ?? mod.route;
}

export function shellAccessFlags(user: ShellAccessUser | null | undefined): Record<string, boolean> {
  const rolesArr = [...(user?.roles ?? [])];
  const rawProfile = user?.permissionProfile;
  const profile = loadedPermissionProfile(rawProfile);
  const view = (section: AppPermissionSectionId) => permissionAllows(rawProfile, section, 'view');
  const flags: Record<string, boolean> = {
    dashboard: view('dashboard'),
    fakturace: view('fakturace'),
    'fronta-prace': view('fronta-prace'),
    'work-report': view('work-report'),
    maps: view('maps'),
    polygons: view('polygons'),
    objects: view('objects'),
    diary: view('diary'),
    navolavani: view('navolavani'),
    inzenyring: view('inzenyring'),
    priprava: view('priprava'),
    email: view('email'),
    datovka: view('datovka'),
    'pd-changes': view('pd-changes'),
    users: view('users') || (profile ? canOpenCompanyList(profile) : false),
    // Kontakty nemají chip v editoru — zůstávají viditelné pro přihlášeného uživatele.
    kontakty: true,
    firmy: profile ? canOpenCompanyList(profile) : false,
    kalendar: view('users-checks'),
    materials: canViewMaterialsSection(rolesArr, rawProfile),
    equipment: EQUIPMENT_HUB_UI_ENABLED && canViewEquipmentSection(rolesArr, rawProfile),
    import: view('import'),
    database: view('database'),
    'app-settings': view('app-settings'),
    // Bez vlastního chipu: Finance na dashboardu, Materiál na dashboardu, Ceník, Výdělek firmy.
    'dashboard-finance': canViewOtherUsersActivityAndWorkReport(rolesArr),
    cenik: hasOrgAdminRole(rolesArr) || hasSuperAdminRole(rolesArr),
    'vydelek-firmy': canReadCompanyEarnings(rolesArr),
  };
  flags['dashboard-material'] =
    flags['dashboard-finance'] && hasWarehouseApiAccessRole(rolesArr);
  return flags;
}

export function shellPathname(url: string): string {
  const path = (url.split(/[?#]/)[0] ?? '').replace(/\/+$/, '');
  return path || '/';
}

export function isShellHomePath(path: string): boolean {
  const p = shellPathname(path);
  return p === SHELL_HOME_PATH || p === '/';
}

/** True on `/app-nastaveni` and its sections (Připojení, Vzhled, …). */
export function isShellSettingsPath(url: string): boolean {
  const path = shellPathname(url);
  return path === '/app-nastaveni' || path.startsWith('/app-nastaveni/');
}

/**
 * Categories for this URL. On Nastavení the module list is replaced by that
 * one category. Everywhere else the modules stay and Nastavení is not one of them.
 */
export function shellSidebarCategories(url: string): readonly ShellModuleDef[] {
  return isShellSettingsPath(url) ? [SHELL_SETTINGS] : SHELL_HOME_MODULES;
}

/** Page to reopen when leaving Nastavení. Anything else falls back to Celkový přehled. */
export function shellSettingsReturnTarget(url: string | null | undefined): string {
  const raw = (url ?? '').split('#')[0].trim();
  if (!raw.startsWith('/') || raw.startsWith('//') || isShellSettingsPath(raw)) {
    return SHELL_LANDING_PATH;
  }
  return raw;
}

/** Label of the page `shellSettingsReturnTarget` opens. */
export function shellSettingsReturnLabel(url: string | null | undefined): string {
  const target = shellSettingsReturnTarget(url);
  return shellSectionFromPath(target)?.label
    ?? shellModuleFromPath(target)?.label
    ?? 'Celkový přehled';
}

function matchSection(url: string): SectionMatch | null {
  const path = shellPathname(url);
  if (isShellHomePath(path)) return null;
  if (path === '/app-nastaveni' || path.startsWith('/app-nastaveni/')) {
    const vzhled = path === '/app-nastaveni/vzhled' || path.startsWith('/app-nastaveni/vzhled/');
    const section = SHELL_SETTINGS.sections.find((item) => item.id === (vzhled ? 'vzhled' : 'pripojeni'));
    return section ? { module: SHELL_SETTINGS, section } : null;
  }
  // Ke schválení lives under Datovka (not under `/datovka/` URL prefix).
  if (path === '/urady/komunikace/k-schvaleni' || path.startsWith('/urady/komunikace/k-schvaleni/')) {
    const priprava = SHELL_HOME_MODULES.find((mod) => mod.id === 'priprava');
    const datovka = priprava?.sections.find((section) => section.id === 'datovka');
    if (priprava && datovka) return { module: priprava, section: datovka };
  }
  for (const match of SECTIONS_BY_ROUTE_LENGTH) {
    if (match.module.id === SHELL_SETTINGS.id) continue;
    const route = match.section?.route;
    if (!route) continue;
    if (path === route || path.startsWith(`${route}/`)) return match;
  }
  return matchLeafModule(path);
}

/** Longest leaf route wins. Section routes are checked first, so they stay more specific. */
function matchLeafModule(path: string): SectionMatch | null {
  let best: ShellModuleDef | null = null;
  for (const mod of ALL_MODULES) {
    if (!shellModuleIsLeaf(mod)) continue;
    const route = mod.route;
    if (path === route || path.startsWith(`${route}/`)) {
      if (!best || route.length > best.route.length) best = mod;
    }
  }
  return best ? { module: best, section: null } : null;
}

/** Longest section route wins, so Firmy (`/users/firmy`) beats Uživatelé (`/users`). */
export function shellSectionFromPath(url: string): ShellSectionDef | null {
  return matchSection(url)?.section ?? null;
}

export function shellModuleFromPath(url: string): ShellModuleDef | null {
  return matchSection(url)?.module ?? null;
}

export interface ShellBack {
  label: string;
  link: string;
}

/**
 * One level up inside a nested entity. Null on Home and on a module's own tabs.
 * Does not jump straight to Home.
 */
export function shellNestedBack(url: string): ShellBack | null {
  const path = shellPathname(url);

  if (/^\/users\/firmy\/[^/]+/.test(path)) {
    return { label: 'Firmy', link: '/users/firmy' };
  }
  if (/^\/users\/(?!firmy$|firmy\/|kalendar$|kalendar\/|kontroly$|kontroly\/|kontakty$|kontakty\/)[^/]+/.test(path)) {
    return { label: 'Uživatelé', link: '/users' };
  }
  if (/^\/polygons\/[^/]+/.test(path)) {
    return { label: 'Polygony', link: '/polygons' };
  }
  if (/^\/objects\/[^/]+/.test(path)) {
    return { label: 'Objekty', link: '/objects' };
  }
  if (/^\/import\/polygons\/[^/]+/.test(path)) {
    return { label: 'Import', link: '/import' };
  }
  if (/^\/fronta-prace\/[^/]+/.test(path)) {
    return { label: 'Fronta práce', link: '/fronta-prace' };
  }
  if (/^\/pd-changes\/[^/]+/.test(path)) {
    return { label: 'Změny oproti PD', link: '/pd-changes' };
  }
  if (/^\/nedodelky\/[^/]+/.test(path)) {
    return { label: 'Nedodělky', link: '/nedodelky' };
  }
  if (/^\/fakturace\/[^/]+/.test(path)) {
    return { label: 'Fakturace', link: '/fakturace' };
  }
  if (/^\/work-report\/user\/[^/]+/.test(path)) {
    return { label: 'Výkaz práce', link: '/work-report' };
  }
  if (/^\/dashboard\/kategorie\/[^/]+/.test(path)) {
    return { label: 'Dashboard', link: '/dashboard' };
  }

  const navItem = /^\/navolavani\/([^/]+)\/item\/[^/]+$/.exec(path);
  if (navItem && navItem[1] !== 'kalendar' && navItem[1] !== 'nastaveni') {
    return { label: 'Navolávání', link: `/navolavani/${navItem[1]}` };
  }
  if (/^\/navolavani\/(?!kalendar$|kalendar\/|nastaveni$|nastaveni\/)[^/]+/.test(path)) {
    return { label: 'Navolávání', link: '/navolavani' };
  }

  return materialsNestedBack(path) ?? equipmentNestedBack(path);
}

function materialsNestedBack(path: string): ShellBack | null {
  if (!path.startsWith('/materials/')) return null;
  if (/^\/materials\/warehouses\/[^/]+$/.test(path)) {
    return { label: 'Sklady', link: '/materials/warehouses' };
  }
  if (/^\/materials\/products\/categories$/.test(path) || /^\/materials\/products\/[^/]+$/.test(path)) {
    return { label: 'Produkty', link: '/materials/products' };
  }
  if (/^\/materials\/warehouse\/stock\/items\/[^/]+$/.test(path)) {
    return { label: 'Zásoby', link: '/materials/warehouse/stock' };
  }
  if (/^\/materials\/warehouse\/inventory\/[^/]+$/.test(path)) {
    return { label: 'Inventury', link: '/materials/warehouse/inventory' };
  }
  if (/^\/materials\/warehouse\/receipts\/[^/]+$/.test(path)) {
    return { label: 'Příjem', link: '/materials/warehouse/receipts' };
  }
  if (/^\/materials\/warehouse\/issues\/[^/]+$/.test(path)) {
    return { label: 'Výdej', link: '/materials/warehouse/issues' };
  }
  if (/^\/materials\/warehouse\/transfers\/[^/]+$/.test(path)) {
    return { label: 'Převod', link: '/materials/warehouse/transfers' };
  }
  if (/^\/materials\/price-comparison\/suppliers\/[^/]+$/.test(path)) {
    return { label: 'Ceny', link: '/materials/price-comparison' };
  }
  if (/^\/materials\/material\/[^/]+$/.test(path)) {
    return { label: 'Katalog', link: '/materials/catalog' };
  }
  return null;
}

const EQUIPMENT_MODULE_SEGMENTS = new Set([
  'stock',
  'calendar',
  'catalog',
  'receipts',
  'issues',
  'transfers',
  'categories',
  'prijeti',
  'prevod',
]);

function equipmentNestedBack(path: string): ShellBack | null {
  if (!path.startsWith('/equipment/')) return null;
  if (/^\/equipment\/receipts\/[^/]+$/.test(path)) {
    return { label: 'Příjem', link: '/equipment/receipts' };
  }
  if (/^\/equipment\/issues\/[^/]+$/.test(path)) {
    return { label: 'Výdej', link: '/equipment/issues' };
  }
  if (/^\/equipment\/transfers\/[^/]+$/.test(path)) {
    return { label: 'Převod', link: '/equipment/transfers' };
  }
  const one = /^\/equipment\/([^/]+)$/.exec(path);
  if (one && !EQUIPMENT_MODULE_SEGMENTS.has(one[1])) {
    return { label: 'Zařízení', link: '/equipment/stock' };
  }
  return null;
}

/**
 * Prefixes of section-tab rows that belong to the module, not to an open entity.
 * On a nested route these rows are hidden so the sidebar shows only the latest level.
 * `users-tab` and `navolavani-tab` are exceptions: the same row swaps to entity tabs.
 */
const MODULE_HUB_TAB_PREFIXES = new Set([
  'dashboard-hub-tab',
  'diary-scope-tab',
  'import-hub-tab',
  'fronta-prace-view-tab',
  'objects-view-tab',
  'polygons-list-tab',
  'urady-hub-tab',
  'equipment-hub-tab',
  'materials-hub-tab',
  'navolavani-tab',
  'users-list-tab',
  'users-tab',
]);

function isEntityTabRow(row: TopbarSectionTabRow, path: string): boolean {
  if (!MODULE_HUB_TAB_PREFIXES.has(row.idPrefix)) return true;
  if (row.idPrefix === 'users-tab' && /^\/users\/firmy\/[^/]+/.test(path)) return true;
  if (
    row.idPrefix === 'navolavani-tab'
    && /^\/navolavani\/(?!kalendar$|kalendar\/|nastaveni$|nastaveni\/)[^/]+/.test(path)
  ) {
    return true;
  }
  return false;
}

/** Sidebar rows for the current level. Empty when the nested page has no section tabs of its own. */
export function shellSidebarRows(rows: readonly TopbarSectionTabRow[], url: string): TopbarSectionTabRow[] {
  const path = shellPathname(url);
  if (isShellHomePath(path)) return [];
  const visible = rows.filter((row) => row.visible !== false && row.tabs.length > 0);
  if (!shellNestedBack(path)) return visible;
  const entityRows = visible.filter((row) => isEntityTabRow(row, path));
  if (entityRows.length === 0) return [];
  return [entityRows[entityRows.length - 1]];
}

/**
 * Module sections replace these hub rows (Dashboard hub, Uživatelé/Kalendář).
 * Polygon portfolio, skladové záložky, úřady and the other in-page tabs stay:
 * they are the only control on that screen.
 */
const SUPERSEDED_WHEN_MODULE_SECTIONS = new Set(['dashboard-hub-tab', 'users-tab']);

export function shellTabsBesideModuleSections(rows: readonly TopbarSectionTabRow[]): TopbarSectionTabRow[] {
  return rows.filter((row) => !SUPERSEDED_WHEN_MODULE_SECTIONS.has(row.idPrefix));
}

/**
 * Which sidebar section owns a tab row. Child rows render under that section,
 * before the next sibling — never after the rest of the module.
 */
export function shellTabRowSectionId(idPrefix: string, url: string): string | null {
  const path = shellPathname(url);
  switch (idPrefix) {
    case 'objects-view-tab':
      return 'objects';
    case 'polygons-list-tab':
    case 'pd-tab':
      return 'polygons';
    case 'diary-scope-tab':
      return 'diary';
    case 'navolavani-tab':
      return 'navolavani';
    case 'pd-changes-tab':
      return 'pd-changes';
    case 'import-hub-tab':
    case 'import-polygon-tab':
      return 'import';
    case 'equipment-hub-tab':
      return 'equipment';
    case 'materials-hub-tab':
      return 'materials';
    case 'user-detail-range-tab':
    case 'asset-card-tab':
    case 'users-list-tab':
      return 'users';
    case 'users-tab':
      return path.includes('/users/firmy') ? 'firmy' : 'users';
    case 'dashboard-material-tab':
      return 'material';
    case 'fronta-prace-view-tab':
    case 'fronta-prace-tab':
      return 'fronta-prace';
    case 'urady-hub-tab':
      if (path.startsWith('/urady/priprava')) return 'priprava';
      if (
        path === '/urady/komunikace/email'
        || path.startsWith('/urady/komunikace/email/')
        || path === '/urady/komunikace/posta'
        || path.startsWith('/urady/komunikace/posta/')
      ) {
        return 'email';
      }
      if (
        path.startsWith('/urady/komunikace/datovka')
        || path.startsWith('/urady/komunikace/k-schvaleni')
      ) {
        return 'datovka';
      }
      if (path.startsWith('/urady/komunikace')) return 'email';
      return 'inzenyring';
    default:
      return null;
  }
}

/** Tab rows that belong directly under one section on this route. */
export function shellRowsForSection(
  sectionId: string,
  rows: readonly TopbarSectionTabRow[],
  url: string,
): TopbarSectionTabRow[] {
  const path = shellPathname(url);
  const currentSection = shellSectionFromPath(path)?.id ?? null;
  const visible = shellNestedBack(path)
    ? shellSidebarRows(rows, url)
    : shellTabsBesideModuleSections(shellSidebarRows(rows, url));
  return visible.filter((row) => {
    const owner = shellTabRowSectionId(row.idPrefix, path);
    if (owner !== sectionId) return false;
    // A stale registration from the previous page must not keep that section open.
    return !currentSection || owner === currentSection;
  });
}

/**
 * Whether this sidebar tab is the current page.
 * Hub tabs (Zařízení, Sklady) match the URL. A stored `activeId` is only a
 * fallback for in-page rows, and never for a section the route has left.
 * `undefined` from the path matcher means "this prefix is not path-mapped".
 */
export function shellTabIsCurrent(
  url: string,
  row: { idPrefix: string; activeId: string },
  tabId: string,
): boolean {
  const path = shellPathname(url);
  const owner = shellTabRowSectionId(row.idPrefix, path);
  const current = shellSectionFromPath(path)?.id ?? null;
  if (!owner || owner !== current) return false;
  const fromUrl = shellHubTabIdFromUrl(url, row.idPrefix);
  if (fromUrl !== undefined) return fromUrl === tabId;
  return tabId === row.activeId;
}

/** Path-mapped hub tab, or `undefined` when the row is an in-page tab. */
function shellHubTabIdFromUrl(url: string, idPrefix: string): string | null | undefined {
  const path = shellPathname(url);
  if (idPrefix === 'equipment-hub-tab') return equipmentTabIdFromPath(path);
  if (idPrefix === 'materials-hub-tab') return materialsTabIdFromPath(path);
  if (idPrefix === 'dashboard-material-tab') return materialDashboardTabIdFromPath(path);
  if (idPrefix === 'pd-tab') {
    const query = new URLSearchParams((url.split('?')[1] ?? '').split('#')[0]);
    return query.get('tab') || 'dashboard';
  }
  return undefined;
}

/**
 * Materiál sub-tabs. The first segment decides the row, so `/dashboard` and
 * `/dashboard/material` cannot steal each other's highlight.
 * `/dashboard/material`, `/dashboard/material/hodnota`, and the retired
 * `/dashboard/material/pohyby` are Hodnota.
 */
function materialDashboardTabIdFromPath(path: string): string | null {
  if (path !== '/dashboard/material' && !path.startsWith('/dashboard/material/')) return null;
  const segment = path.slice('/dashboard/material'.length).split('/').filter(Boolean)[0] ?? '';
  if (segment === '' || segment === 'hodnota' || segment === 'pohyby') return 'hodnota';
  if (segment === 'prijem-vydej') return 'prijem-vydej';
  if (segment === 'pod-minimem') return 'pod-minimem';
  return 'hodnota';
}

/** Longest Zařízení prefix wins. Paths outside `/equipment` match nothing. */
function equipmentTabIdFromPath(path: string): string | null {
  if (path !== '/equipment' && !path.startsWith('/equipment/')) return null;
  if (path.startsWith('/equipment/receipts')) return 'equipment-receipts';
  if (path.startsWith('/equipment/issues')) return 'equipment-issues';
  if (
    path.startsWith('/equipment/transfers')
    || path.startsWith('/equipment/prijeti')
    || path.startsWith('/equipment/prevod')
  ) {
    return 'equipment-transfers';
  }
  if (
    path === '/equipment/categories'
    || path.startsWith('/equipment/categories/')
    || path.startsWith('/equipment/catalog/categories')
  ) {
    return 'equipment-categories';
  }
  if (path.startsWith('/equipment/catalog')) return 'equipment-catalog';
  if (path.startsWith('/equipment/calendar')) return 'equipment-calendar';
  return 'equipment-stock';
}

/**
 * Longest Sklady prefix wins.
 * `/materials/warehouses` must not match `/materials/warehouse/…`.
 */
function materialsTabIdFromPath(path: string): string | null {
  if (path !== '/materials' && !path.startsWith('/materials/')) return null;
  if (path === '/materials/warehouses' || path.startsWith('/materials/warehouses/')) return 'warehouses';
  if (path.startsWith('/materials/products')) return 'products';
  if (path.startsWith('/materials/warehouse/stock')) return 'warehouse-stock';
  if (path.startsWith('/materials/warehouse/receipts')) return 'warehouse-receipts';
  if (path.startsWith('/materials/warehouse/issues')) return 'warehouse-issues';
  if (path.startsWith('/materials/warehouse/transfers')) return 'warehouse-transfers';
  if (path.startsWith('/materials/warehouse/inventory')) return 'warehouse-inventory';
  if (path.startsWith('/materials/warehouse/labels')) return 'catalog';
  if (path.startsWith('/materials/replenishment')) return 'replenishment';
  if (path.startsWith('/materials/price-comparison') || path.startsWith('/materials/procurement')) {
    return 'price-comparison';
  }
  if (path.startsWith('/materials/catalog') || path.startsWith('/materials/material/')) return 'catalog';
  if (path === '/materials') return 'warehouse-stock';
  return null;
}

export interface ShellCrumb {
  label: string;
  link: string | null;
}

export function shellBreadcrumbs(input: {
  url: string;
  pageTitle: string;
  pageSubtitle: string;
  activeTabLabel: string;
}): ShellCrumb[] {
  const path = shellPathname(input.url);
  if (isShellHomePath(path)) {
    return [{ label: SHELL_HOME_TITLE, link: null }];
  }
  const mod = shellModuleFromPath(path);
  const title = firstLine(input.pageTitle);
  const subtitle = firstLine(input.pageSubtitle);
  const tab = firstLine(input.activeTabLabel);
  if (!mod) {
    const label = title || 'TelcoTrack';
    const crumbs: ShellCrumb[] = [{ label, link: null }];
    if (tab && !sameText(tab, label)) crumbs.push({ label: tab, link: null });
    return sealCrumbs(crumbs);
  }

  const section = shellSectionFromPath(path);
  const sectionLabel = section?.label ?? '';
  const crumbs: ShellCrumb[] = [{ label: mod.label, link: mod.route }];
  if (section && !sameText(section.label, mod.label)) {
    crumbs.push({ label: section.label, link: section.route });
  }
  const nested = !!shellNestedBack(path);
  if (nested) {
    const entity = title && !sameText(title, mod.label) && !sameText(title, sectionLabel)
      ? title
      : subtitle && !sameText(subtitle, mod.label) && !sameText(subtitle, sectionLabel)
        ? subtitle
        : '';
    if (entity) crumbs.push({ label: entity, link: null });
  }
  const lastLabel = crumbs[crumbs.length - 1]?.label ?? '';
  if (
    tab
    && !sameText(tab, lastLabel)
    && !sameText(tab, mod.label)
    && !sameText(tab, sectionLabel)
  ) {
    crumbs.push({ label: tab, link: null });
  } else if (
    !nested
    && title
    && !sameText(title, mod.label)
    && !sameText(title, sectionLabel)
    && !sameText(title, lastLabel)
  ) {
    crumbs.push({ label: title, link: null });
  }
  return sealCrumbs(crumbs);
}

function sealCrumbs(crumbs: ShellCrumb[]): ShellCrumb[] {
  if (crumbs.length === 0) return crumbs;
  const last = crumbs.length - 1;
  return crumbs.map((crumb, index) => (index === last ? { ...crumb, link: null } : crumb));
}

function firstLine(value: string): string {
  return (value ?? '').split('\n')[0]?.trim() ?? '';
}

function sameText(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase('cs') === b.trim().toLocaleLowerCase('cs');
}
